import { Link } from 'react-router-dom';
import { Brain, Sparkles, Target, Zap, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTenant } from '../contexts/TenantContext';

export default function HomePage() {
  const tenant = useTenant();
  const isKids = tenant.slug === 'kids';

  return (
    <div className="min-h-screen" style={{ background: tenant.theme.colors.gradients.main }}>
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="container mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Logo dinámico */}
            <div className="text-6xl mb-6 animate-bounce">
              {tenant.content.logoIcon}
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: tenant.theme.fonts.primary.family }}>
              {isKids ? (
                <>
                  ¡Hola! 👋
                  <br />
                  <span style={{ color: tenant.theme.colors.accent }}>
                    {tenant.content.tagline}
                  </span>
                </>
              ) : (
                <>
                  Despierta tu mente
                  <br />
                  <span style={{ color: tenant.theme.colors.accent }}>
                    Descubre tu fuerza
                  </span>
                </>
              )}
            </h1>

            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
              {tenant.content.homeHero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="text-lg px-8 py-4 rounded-lg font-bold transition-transform hover:scale-105"
                style={{
                  background: tenant.theme.colors.accent,
                  color: isKids ? '#fff' : '#000',
                }}
              >
                {isKids ? '¡Empezar a jugar!' : 'Comenzar gratis'}
              </Link>
              <Link
                to="/juegos"
                className="text-lg px-8 py-4 rounded-lg font-semibold border-2 transition-transform hover:scale-105"
                style={{
                  borderColor: 'rgba(255,255,255,0.3)',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                }}
              >
                Ver juegos
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Elementos decorativos */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-pulse-slow opacity-20"
            style={{ background: tenant.theme.colors.accent }}
          />
          <div
            className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-pulse-slow opacity-20"
            style={{ background: tenant.theme.colors.primary, animationDelay: '1s' }}
          />
        </div>
      </section>

      {/* Características */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Brain, title: 'Memoria', color: 'text-blue-400' },
              { icon: Target, title: 'Atención', color: 'text-green-400' },
              { icon: Zap, title: 'Agilidad Mental', color: 'text-yellow-400' },
              { icon: Sparkles, title: 'Flexibilidad', color: 'text-purple-400' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="glass-container text-center group hover:scale-105 transition-transform"
              >
                <item.icon className={`w-16 h-16 mx-auto mb-4 ${item.color} group-hover:scale-110 transition-transform`} />
                <h3 className="text-xl font-semibold">{item.title}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-display font-bold mb-12">¿Cómo funciona?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '1', title: 'Elige tu juego', desc: 'Selecciona entre más de 15 puzzles lógicos diferentes' },
              { num: '2', title: 'Entrena', desc: 'Resuelve puzzles adaptados a tu nivel de dificultad' },
              { num: '3', title: 'Mejora', desc: 'Sigue tu progreso y observa cómo mejoras día a día' },
            ].map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.2 }}
                className="glass-container"
              >
                <div className="w-16 h-16 bg-neureka-yellow rounded-full flex items-center justify-center text-3xl font-bold text-black mx-auto mb-4">
                  {step.num}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-white/70">{step.desc}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-12"
          >
            <Link to="/register" className="btn-neureka text-lg px-8 py-4 inline-block">
              Crear cuenta gratuita
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="glass-container text-center max-w-3xl mx-auto">
            <TrendingUp className="w-20 h-20 text-neureka-yellow mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Empieza a entrenar tu mente hoy
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Únete a miles de usuarios que ya están mejorando sus habilidades cognitivas
            </p>
            <Link to="/register" className="btn-neureka text-lg px-8 py-4 inline-block">
              Comenzar ahora - Es gratis
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
